<template>
  <div class="device-targeting-config">
    <!-- 顶部说明栏 -->
    <div class="description-bar">
      <div class="description-left">
        <p class="desc-main">广告定投主要面向媒体接入测试及日常测试；支持在设备维度进行定向投放配置，提升媒体测试效率。</p>
        <ul>
          <li>支持对设备定向广告类型预览（包括H5类、下载类、Deeplink调起类）；</li>
          <li>每个账户最多支持同时在线 5 条定投。每条配置最多在线 3 天。</li>
          <li>新建 / 修改后，线上 20 分钟后生效。</li>
        </ul>
      </div>
      <div class="description-right">
        当前在线定投数 {{ onlineCount }} / 5
      </div>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-form :model="queryCondition" inline class="search-form">
        <el-form-item label="设备标识">
          <el-input
            v-model="queryCondition.deviceId"
            placeholder="请输入单个OAID / IMEI / IDFA"
            clearable
            style="width: 260px"
          />
        </el-form-item>
        <el-form-item label="操作系统">
          <el-select v-model="queryCondition.operatingSystem" placeholder="请选择" clearable style="width: 120px">
            <el-option label="Android" value="Android" />
            <el-option label="iOS" value="iOS" />
            <el-option label="鸿蒙" value="鸿蒙" />
          </el-select>
        </el-form-item>
        <el-form-item label="定投广告类型">
          <el-select v-model="queryCondition.adType" placeholder="请选择" clearable style="width: 180px">
            <el-option
              v-for="opt in adTypeOptions"
              :key="opt.id"
              :label="opt.name"
              :value="opt.name"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="是否在线">
          <el-select v-model="queryCondition.isOnline" placeholder="请选择" clearable style="width: 100px">
            <el-option label="是" value="是" />
            <el-option label="否" value="否" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="onlineCount < 5">
          <el-button type="primary" @click="openAddDialog">新建定投</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 设备定投列表 -->
    <div class="table-wrap">
      <el-table :data="curList" border stripe>
        <el-table-column prop="number" label="编号" width="70" />
        <el-table-column prop="deviceId" label="设备标识" min-width="180" show-overflow-tooltip />
        <el-table-column prop="deviceIdType" label="设备标识类型" width="120">
          <template slot-scope="scope">{{ scope.row.deviceIdType || '—' }}</template>
        </el-table-column>
        <el-table-column prop="operatingSystem" label="操作系统" width="100" />
        <el-table-column prop="adTypeName" label="定投广告类型" width="140" />
        <el-table-column prop="expireTime" label="到期时间" width="170" />
        <el-table-column prop="isEffective" label="是否生效" width="90" />
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button type="text" size="small" @click="handleDelete(scope.row)">删除</el-button>
            <el-button v-if="scope.row.isEffective === '否'" type="text" size="small" @click="handleOnline(scope.row)">上线</el-button>
            <el-button v-else type="text" size="small" @click="handleOffline(scope.row)">下线</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="footer">
        <el-pagination
          :current-page="curPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          :total="filterList.length"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="720px"
      @close="dialogVisible = false"
    >
      <el-form
        ref="dialogForm"
        :model="dialogForm"
        :rules="dialogRules"
        label-width="120px"
        class="dialog-form"
      >
        <div class="dialog-tip">
          配置定投广告的预估收益、eCPM、展示量、点击量均不计入数据报表，其它正常计入。
        </div>

        <!-- 操作系统 -->
        <el-form-item prop="os" :required="false">
          <span slot="label"><span class="required-star">*</span>操作系统(平台)</span>
          <el-radio-group v-model="dialogForm.os" :disabled="!!editingId">
            <el-radio label="Android">Android</el-radio>
            <el-radio label="iOS">iOS</el-radio>
            <el-radio label="鸿蒙">鸿蒙</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 设备 ID（类型 + 填写框） -->
        <el-form-item prop="deviceIdCombo" :required="false">
          <span slot="label"><span class="required-star">*</span>设备ID</span>
          <el-select
            v-model="dialogForm.deviceIdType"
            placeholder="请选择"
            style="width: 120px"
            :disabled="!!editingId"
          >
            <el-option
              v-for="opt in deviceIdTypeOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-input
            v-model="dialogForm.deviceId"
            placeholder="请输入"
            style="width: 360px; margin-left: 12px"
            :disabled="!!editingId"
          />
        </el-form-item>

        <!-- 流量范围（流量类型 + 流量ID） -->
        <el-form-item prop="trafficCombo" :required="false">
          <span slot="label"><span class="required-star">*</span>流量范围</span>
          <el-select
            v-model="dialogForm.trafficType"
            placeholder="流量类型"
            style="width: 140px"
          >
            <el-option
              v-for="opt in trafficTypeOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-select
            v-model="dialogForm.trafficId"
            placeholder="请选择"
            filterable
            style="width: 360px; margin-left: 12px"
            :disabled="!dialogForm.trafficType"
          >
            <el-option
              v-for="opt in trafficIdOptions"
              :key="opt.id"
              :label="opt.label"
              :value="opt.id"
            />
          </el-select>
        </el-form-item>

        <!-- 广告预算类型 -->
        <el-form-item prop="adBudgetType" :required="false">
          <span slot="label"><span class="required-star">*</span>广告预算类型</span>
          <el-select
            v-model="dialogForm.adBudgetType"
            placeholder="请选择广告类型"
            style="width: 260px"
          >
            <el-option
              v-for="opt in adBudgetTypeOptions"
              :key="opt.id"
              :label="opt.name"
              :value="opt.name"
            />
          </el-select>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <span class="dialog-bottom-text">配置后预计20分钟后可以在测试设备上预览。</span>
        <div class="dialog-footer-buttons">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleDialogConfirm">确认</el-button>
        </div>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { sspApi } from '@/api'

const MAX_ONLINE = 5
const AD_TYPE_OPTIONS = [
  { id: 'app_direct', name: 'App-直接下载' },
  { id: 'app_landing', name: 'App-落地页下载' },
  { id: 'app_vendor', name: 'App-厂商下载' },
  { id: 'app_store', name: 'App-App Store下载' },
  { id: 'deeplink', name: 'Deeplink唤起类' },
  { id: 'h5', name: 'H5落地页' },
  { id: 'quick_app', name: '快应用' },
  { id: 'wechat_mini_program', name: '微信小程序' }
]

export default {
  name: 'DeviceTargetingConfig',
  data() {
    return {
      queryCondition: {
        deviceId: '',
        operatingSystem: '',
        adType: '',
        isOnline: ''
      },
      list: [],
      curPage: 1,
      pageSize: 20,
      dialogVisible: false,
      dialogTitle: '新建定投测试配置',
      adTypeOptions: AD_TYPE_OPTIONS,
      dialogForm: {
        os: 'Android',
        deviceIdType: 'OAID',
        deviceId: '',
        trafficType: '',
        trafficId: '',
        adBudgetType: '',
        deviceIdCombo: 0,
        trafficCombo: 0
      },
      dialogRules: {
        os: [
          { required: true, message: '请选择操作系统', trigger: 'change' }
        ],
        deviceIdCombo: [
          { validator: (rule, value, callback) => this.validateDeviceId(rule, value, callback), trigger: 'change' }
        ],
        trafficCombo: [
          { validator: (rule, value, callback) => this.validateTraffic(rule, value, callback), trigger: 'change' }
        ],
        adBudgetType: [
          { required: true, message: '请选择广告预算类型', trigger: 'change' }
        ]
      },
      deviceIdTypeAllOptions: [
        { value: 'IMEI', label: 'IMEI' },
        { value: 'OAID', label: 'OAID' },
        { value: 'IDFA', label: 'IDFA' }
      ],
      trafficTypeOptions: [
        { value: 'adzone', label: '广告位' },
        { value: 'media', label: '媒体' }
      ],
      adzoneOptions: [],
      mediaOptions: [],
      // 标记是否为编辑态下的首次回显，避免 watch 把回显的值清空
      isEditingTrafficType: false,
      // 当前编辑的配置 ID（为空表示新建）
      editingId: null
    }
  },
  computed: {
    filterList() {
      let list = this.list
      const deviceId = (this.queryCondition.deviceId || '').trim()
      if (deviceId) {
        list = _.filter(list, (it) => (it.deviceId || '').indexOf(deviceId) !== -1)
      }
      if (this.queryCondition.operatingSystem) {
        list = _.filter(list, (it) => it.operatingSystem === this.queryCondition.operatingSystem)
      }
      if (this.queryCondition.adType) {
        list = _.filter(list, (it) => it.adType === this.queryCondition.adType)
      }
      if (this.queryCondition.isOnline) {
        list = _.filter(list, (it) => it.isEffective === this.queryCondition.isOnline)
      }
      return list
    },
    sortedList() {
      return this.filterList.map((item, index) => ({
        ...item,
        number: index + 1,
        adTypeName: this.getAdTypeName(item.adType)
      }))
    },
    curList() {
      const start = (this.curPage - 1) * this.pageSize
      return _.slice(this.sortedList, start, start + this.pageSize)
    },
    onlineCount() {
      return _.filter(this.list, (it) => it.isEffective === '是').length
    },
    adBudgetTypeOptions() {
      const os = this.dialogForm.os
      if (os === 'Android' || os === '鸿蒙') {
        const allowNames = ['App-直接下载', 'App-厂商下载', 'App-App Store下载', 'Deeplink唤起类', 'H5落地页', '快应用', '微信小程序']
        return this.adTypeOptions.filter((it) => allowNames.indexOf(it.name) !== -1)
      }
      if (os === 'iOS') {
        const allowNames = ['App-App Store下载', 'Deeplink唤起类', 'H5落地页', '微信小程序']
        return this.adTypeOptions.filter((it) => allowNames.indexOf(it.name) !== -1)
      }
      return this.adTypeOptions
    },
    deviceIdTypeOptions() {
      const os = this.dialogForm.os
      if (os === 'Android' || os === '鸿蒙') {
        return this.deviceIdTypeAllOptions.filter((o) => o.value === 'IMEI' || o.value === 'OAID')
      }
      if (os === 'iOS') {
        return this.deviceIdTypeAllOptions.filter((o) => o.value === 'IDFA')
      }
      return this.deviceIdTypeAllOptions
    },
    trafficIdOptions() {
      if (this.dialogForm.trafficType === 'adzone') {
        return this.adzoneOptions
      }
      if (this.dialogForm.trafficType === 'media') {
        return this.mediaOptions
      }
      return []
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    'dialogForm.os'(val) {
      if (val === 'Android' || val === '鸿蒙') {
        this.dialogForm.deviceIdType = 'OAID'
      } else if (val === 'iOS') {
        this.dialogForm.deviceIdType = 'IDFA'
      }
      this.dialogForm.deviceId = ''
      // 新建时切换操作系统需要清空广告预算类型；编辑态下保留后端回显的值
      if (!this.editingId) {
        this.dialogForm.adBudgetType = ''
      }
    },
    'dialogForm.trafficType'(val) {
      // 编辑模式下的首次赋值不清空 trafficId，仅用于回显
      if (this.isEditingTrafficType) {
        this.isEditingTrafficType = false
      } else {
        this.dialogForm.trafficId = ''
      }
      if (val === 'adzone' && this.adzoneOptions.length === 0) {
        this.loadAdzoneOptions()
      } else if (val === 'media' && this.mediaOptions.length === 0) {
        this.loadMediaOptions()
      }
    }
  },
  methods: {
    getAdTypeName(adType) {
      return adType || '—'
    },
    loadData() {
      const accountId = this.$store.state.accountInfo?.id
      if (!accountId) {
        this.$notify.error({ title: '错误', message: '无法获取账户信息' })
        return
      }
      sspApi.listDeviceTargetingConfig({ params: { accountId } })
        .then((res) => {
          const raw = Array.isArray(res) ? res : (res && (res.list || res.data)) || []
          this.list = raw.map((item) => ({
            id: item.id,
            deviceId: item.deviceId || item.device_id || '',
            deviceIdType: item.deviceIdType || item.device_id_type || '',
            operatingSystem: item.operatingSystem || item.os || item.operating_system || '',
            // 流量范围类型 & 目标ID
            trafficScopeType: item.trafficScopeType || item.traffic_scope_type || '',
            targetId: item.targetId || item.target_id || '',
            // 直接使用后端 campaignType 作为广告预算类型（推广目标）
            adType: (() => {
              const rawType = item.campaignType || item.campaign_type || item.adType || item.ad_type || ''
              const opt = _.find(AD_TYPE_OPTIONS, (o) => o.id === rawType)
              return opt ? opt.name : rawType
            })(),
            // 到期时间：优先使用后端 expiredTime 字段
            expireTime: item.expiredTime || item.expired_time || item.expireTime || item.expire_time || item.expireTimeStr || '',
            // 后端 status = 1 表示上线，其余为否，兼容旧字段
            isEffective: (item.status === 1 || item.status === '1') ||
              item.isEffective === '是' || item.isEffective === true ||
              item.is_effective === '是' || item.is_effective === true ||
              item.isOnline === '是' || item.is_online === '是'
              ? '是'
              : '否'
          }))
        })
        .catch((err) => {
          this.list = []
          if (err && err.response && err.response.status !== 404) {
            this.$rejectAjaxError()(err)
          }
        })
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.curPage = 1
    },
    handleCurrentChange(val) {
      this.curPage = val
    },
    openAddDialog() {
      this.resetDialogForm()
      this.isEditingTrafficType = false
      this.editingId = null
      this.dialogTitle = '新建定投测试配置'
      this.dialogVisible = true
    },
    openEditDialog(row) {
      // 先重置，再根据行数据回填
      this.resetDialogForm()
      this.isEditingTrafficType = false
      this.editingId = row.id
      this.dialogTitle = '编辑'

      // 操作系统
      const os = row.operatingSystem || row.os || ''
      if (os) {
        this.dialogForm.os = os
      }
      // 设备 ID 类型 & 值
      this.dialogForm.deviceIdType = row.deviceIdType || ''
      this.dialogForm.deviceId = row.deviceId || ''

      // 流量范围：trafficScopeType + targetId
      const scope = row.trafficScopeType || row.traffic_scope_type || ''
      const targetId = row.targetId || row.target_id || ''
      if (scope === 'ADZONE') {
        this.isEditingTrafficType = true
        this.dialogForm.trafficType = 'adzone'
        this.dialogForm.trafficId = targetId
        if (!this.adzoneOptions.length) {
          this.loadAdzoneOptions()
        }
      } else if (scope === 'MEDIA') {
        this.isEditingTrafficType = true
        this.dialogForm.trafficType = 'media'
        this.dialogForm.trafficId = targetId
        if (!this.mediaOptions.length) {
          this.loadMediaOptions()
        }
      }

      // 广告预算类型 / 推广目标
      {
        const rawType = row.adType || row.campaignType || row.campaign_type || ''
        const opt = _.find(AD_TYPE_OPTIONS, (o) => o.id === rawType)
        this.dialogForm.adBudgetType = opt ? opt.name : rawType
      }

      // 再 nextTick 强制回写一次，避免 watcher 等副作用导致不回显（尤其是 IDFA 场景）
      this.$nextTick(() => {
        this.dialogForm.deviceIdType = row.deviceIdType || this.dialogForm.deviceIdType
        this.dialogForm.deviceId = row.deviceId || this.dialogForm.deviceId
      })

      this.dialogVisible = true
    },
    handleDelete(row) {
      this.$confirm('确定删除该定投配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        sspApi.deleteDeviceTargetingConfig({ params: { id: row.id } })
          .then(() => {
            this.$notify.success({ title: '成功', message: '删除成功' })
            this.loadData()
          })
          .catch(this.$rejectAjaxError())
      }).catch(() => {})
    },
    handleOnline(row) {
      if (this.onlineCount >= MAX_ONLINE) {
        this.$notify.warning({ title: '提示', message: '配置数超限，最多同时在线 5 条定投' })
        return
      }
      this.$confirm('确定上线该定投配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 上线：status 传 1
        sspApi.switchDeviceTargetingConfig({ params: { id: row.id, status: 1 } })
          .then(() => {
            this.$notify.success({ title: '成功', message: '上线成功' })
            this.loadData()
          })
          .catch((e) => {
            const msg = (e && e.response && e.response.data && e.response.data.message) || ''
            if (msg.indexOf('超限') !== -1 || msg.indexOf('quota') !== -1) {
              this.$notify.warning({ title: '提示', message: '配置数超限，最多同时在线 5 条定投' })
            } else {
              this.$rejectAjaxError()(e)
            }
          })
      }).catch(() => {})
    },
    handleOffline(row) {
      this.$confirm('确定下线该定投配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 下线：status 传 2（1=上线，2=下线）
        sspApi.switchDeviceTargetingConfig({ params: { id: row.id, status: 2 } })
          .then(() => {
            this.$notify.success({ title: '成功', message: '下线成功' })
            this.loadData()
          })
          .catch(this.$rejectAjaxError())
      }).catch(() => {})
    },
    validateDeviceId(rule, value, callback) {
      if (!this.dialogForm.os) {
        callback(new Error('请选择操作系统'))
        return
      }
      if (!this.dialogForm.deviceIdType) {
        callback(new Error('请选择设备ID类型'))
        return
      }
      if (!this.dialogForm.deviceId || !this.dialogForm.deviceId.trim()) {
        callback(new Error('请输入设备ID'))
        return
      }
      callback()
    },
    validateTraffic(rule, value, callback) {
      if (!this.dialogForm.trafficType) {
        callback(new Error('请选择流量类型'))
        return
      }
      if (!this.dialogForm.trafficId) {
        callback(new Error('请选择流量ID'))
        return
      }
      callback()
    },
    resetDialogForm() {
      this.dialogForm = {
        os: 'Android',
        deviceIdType: 'OAID',
        deviceId: '',
        trafficType: '',
        trafficId: '',
        adBudgetType: '',
        deviceIdCombo: 0,
        trafficCombo: 0
      }
    },
    loadAdzoneOptions() {
      sspApi.listAdzone()
        .then((res) => {
          const list = Array.isArray(res) ? res : (res && (res.list || res.data)) || []
          this.adzoneOptions = list.map((it) => ({
            id: it.adzoneId || it.id,
            label: ((it.adzoneId || it.id) + ' - ' + (it.adzoneName || it.name || '')).trim()
          }))
        })
        .catch(this.$rejectAjaxError())
    },
    loadMediaOptions() {
      sspApi.listMedia()
        .then((res) => {
          const list = Array.isArray(res) ? res : (res && (res.list || res.data)) || []
          this.mediaOptions = list.map((it) => ({
            id: it.id,
            label: ((it.id || '') + ' - ' + (it.name || '')).trim()
          }))
        })
        .catch(this.$rejectAjaxError())
    },
    handleDialogConfirm() {
      if (!this.$refs.dialogForm) {
        return
      }
      this.$refs.dialogForm.validate((valid) => {
        if (!valid) {
          return
        }
        const payload = {
          // os：Android / iOS / 鸿蒙
          os: this.dialogForm.os,
          // 设备 ID 类型：IMEI / OAID / IDFA
          deviceIdType: this.dialogForm.deviceIdType,
          // 设备 ID 值
          deviceId: (this.dialogForm.deviceId || '').trim(),
          // 流量范围类型：ADZONE / MEDIA
          trafficScopeType: this.dialogForm.trafficType === 'adzone'
            ? 'ADZONE'
            : this.dialogForm.trafficType === 'media'
              ? 'MEDIA'
              : '',
          // 目标 ID：广告位ID或媒体ID
          targetId: this.dialogForm.trafficId,
          // 广告预算/投放类型：推广目标
          campaignType: this.dialogForm.adBudgetType
        }
        // 根据是否有 editingId 判断是新建还是编辑
        const isEdit = !!this.editingId
        const api = isEdit ? sspApi.editDeviceTargetingConfig : sspApi.createDeviceTargetingConfig
        const data = isEdit ? { id: this.editingId, ...payload } : payload

        api({ data })
          .then(() => {
            this.$notify.success({
              title: '成功',
              message: isEdit ? '定投配置已更新' : '新建定投配置已保存'
            })
            this.dialogVisible = false
            this.loadData()
          })
          .catch(this.$rejectAjaxError())
      })
    }
  }
}
</script>

<style scoped>
.device-targeting-config {
  padding: 0 10px 20px;
}
.description-bar {
  border: 1px solid #e9e9e9;
  padding: 16px 20px;
  margin-bottom: 16px;
  background: #fafafa;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.description-left {
  flex: 1;
  font-size: 14px;
  color: #606266;
}
.desc-main {
  margin: 0 0 10px 0;
  line-height: 1.6;
}
.description-left ul {
  margin: 0;
  padding-left: 20px;
  line-height: 1.8;
}
.description-right {
  flex-shrink: 0;
  margin-left: 20px;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}
.search-bar {
  border: 1px solid #e9e9e9;
  padding: 15px;
  margin-bottom: 0;
}
.search-form >>> .el-form-item {
  margin-right: 20px;
  margin-bottom: 10px;
}
.table-wrap {
  margin-top: 0;
  border: 1px solid #e9e9e9;
  border-top: none;
}
.footer {
  padding: 15px;
  border: 1px solid #e9e9e9;
  border-top: none;
  min-height: 20px;
}
.dialog-form {
  padding: 10px 10px 0;
}
.dialog-tip {
  margin: 0 0 16px 0;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
}
.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.dialog-bottom-text {
  font-size: 12px;
  color: #909399;
}
.dialog-footer-buttons {
  text-align: right;
}
.required-star {
  color: #f56c6c;
  margin-right: 4px;
}
</style>
